import { useCallback, useEffect, useRef, useState } from 'react';
import { ArdoqId } from '@ardoq/api-types';
import { ScrollPositions, TableScrollIndex } from '../types';
import { CELL_SIZE } from '../consts';
import { scrollIndexShouldUpdate } from '../utils';

type UseTableScrollIndexArgs = {
  sourceIds: ArdoqId[];
  selectedComponentId: ArdoqId;
  sourceName: string | null | undefined;
  containerRef: React.RefObject<HTMLDivElement | null>;
  tableHeadRef: React.RefObject<HTMLTableHeaderCellElement | null>;
};

type UseTableScrollIndexResult = {
  tableScrollIndexes: TableScrollIndex;
  updateScrollIndex: (scrollPosition: ScrollPositions) => void;
};

/**
 * Manages the virtual scroll index for the Dependency Matrix table and handles
 * scrolling to the selected component when it changes.
 *
 * Scroll-to-selected uses a two-pass approach to handle virtual scrolling:
 *
 * 1. When `selectedComponentId` changes, `verticalIndex` is updated during the
 *    same render to bring the selected row into the virtual render window. If
 *    the row is still not in the DOM, an approximate `scrollTop` is set using
 *    index * CELL_SIZE. This is inexact because rows can be taller than
 *    CELL_SIZE when a cell contains multiple references.
 *
 * 2. Setting `scrollTop` fires a scroll event, which updates `tableScrollIndexes`
 *    via `updateScrollIndex`. This re-runs the scroll effect, which now finds
 *    the row in the DOM and uses the precise `row.offsetTop` measurement.
 */
export const useTableScrollIndex = ({
  sourceIds,
  selectedComponentId,
  sourceName,
  containerRef,
  tableHeadRef,
}: UseTableScrollIndexArgs): UseTableScrollIndexResult => {
  const [tableScrollIndexes, setTableScrollIndexes] =
    useState<TableScrollIndex>(() => ({
      verticalIndex: Math.max(0, sourceIds.indexOf(selectedComponentId)),
      horizontalIndex: 0,
    }));

  const [scrollTarget, setScrollTarget] = useState({
    selectedComponentId,
    sourceIds,
  });
  if (
    scrollTarget.selectedComponentId !== selectedComponentId ||
    scrollTarget.sourceIds !== sourceIds
  ) {
    setScrollTarget({ selectedComponentId, sourceIds });
    const selectedIndex = sourceIds.indexOf(selectedComponentId);
    if (selectedIndex !== -1) {
      setTableScrollIndexes(prev => ({
        ...prev,
        verticalIndex: selectedIndex,
      }));
    }
  }

  const pendingScrollToSelected = useRef(false);

  useEffect(() => {
    if (sourceIds.includes(selectedComponentId)) {
      pendingScrollToSelected.current = true;
    }
  }, [selectedComponentId, sourceIds]);

  useEffect(() => {
    if (!pendingScrollToSelected.current) return;
    if (!containerRef.current) return;

    const selectedIndex = sourceIds.indexOf(selectedComponentId);
    const row = containerRef.current.querySelector<HTMLElement>(
      `tr[data-component-id="${selectedComponentId}"]`
    );
    if (!row && selectedIndex === -1) return;

    const headerHeight = tableHeadRef.current?.offsetHeight ?? 0;
    const { scrollTop, clientHeight } = containerRef.current;

    if (!row) {
      // Row is outside the virtual render window. Scroll approximately into
      // view using index-based position; the row will render, triggering another
      // tableScrollIndexes change that re-runs this effect with the real DOM node.
      const approximateRowTop =
        (sourceName ? CELL_SIZE : 0) + selectedIndex * CELL_SIZE;
      containerRef.current.scrollTop = approximateRowTop - headerHeight;
      return;
    }

    pendingScrollToSelected.current = false;

    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    const visibleTop = scrollTop + headerHeight;
    const visibleBottom = scrollTop + clientHeight;
    if (rowTop < visibleTop || rowBottom > visibleBottom) {
      containerRef.current.scrollTop = rowTop - headerHeight;
    }
  }, [
    tableScrollIndexes,
    selectedComponentId,
    sourceIds,
    sourceName,
    containerRef,
    tableHeadRef,
  ]);

  const updateScrollIndex = useCallback(
    (scrollPosition: ScrollPositions) => {
      const { horizontalIndex, verticalIndex } = tableScrollIndexes;

      const newHorizontalIndex = Math.round(scrollPosition.left / CELL_SIZE);
      const newVerticalIndex = Math.round(scrollPosition.top / CELL_SIZE);

      if (
        scrollIndexShouldUpdate(horizontalIndex, newHorizontalIndex) ||
        scrollIndexShouldUpdate(verticalIndex, newVerticalIndex)
      ) {
        setTableScrollIndexes({
          horizontalIndex: newHorizontalIndex,
          verticalIndex: newVerticalIndex,
        });
      }
    },
    [tableScrollIndexes]
  );

  return { tableScrollIndexes, updateScrollIndex };
};
